import React, { useState, useEffect, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import SplashScreen from './ui/components/SplashScreen';
import { Box } from '@mui/material';
import Users from './pages/Users';
import { UserProvider } from './modules/user/UserContext';
import Exhibitors from './pages/Exhibitors';
import Settings from './pages/Settings';
import Payments from './pages/Payments';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CASHandler from './utils/CASHandler';
import ProtectedRoute from './utils/ProtectedRoute';
import Profile from './pages/Profile';
import Image from './utils/Image';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

// Use React.lazy to load components lazily
const Root = React.lazy(() => import('./pages/Root'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Events = React.lazy(() => import('./pages/Events'));
const Bookings = React.lazy(() => import('./pages/Bookings'));
const Players = React.lazy(() => import('./pages/Players'));
const AddEditBooking = React.lazy(() => import('./pages/AddEditBooking'));
const AddEditEvent=React.lazy(()=> import('./pages/AddEditEvent'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const FeedbackForm = React.lazy(() => import('./pages/FeedbackForm'));

const queryClient = new QueryClient();

// Define public routes
const publicRoutes = [
  { path: "/", element: <CASHandler setLoading={() => {}} /> },
  { path: "*", element: <NotFound /> }, 
  {path:"/feedback", element: <FeedbackForm />},
];

// Define protected routes
const protectedRoutes = [
  { path: "/dash", element: <Dashboard /> },
  { path: "/profile", element: <Profile /> },
  { path: "/events", element: <Events /> },
  { path: "/bookings/add", element: <AddEditBooking /> },
  { path: "/bookings/add/:bookingId", element: <AddEditBooking />, adminOnly: true },
  { path: "/bookings", element: <Bookings /> },
  { path: "/players", element: <Players /> },
  { path: "/users", element: <Users /> },
  { path: "/exhibitors", element: <Exhibitors /> },
  { path: "/feedback", element: <FeedbackForm /> },
  { path: "/settings", element: <Settings />, adminOnly: true },
  { path: "/payments", element: <Payments /> },
  { path: "/events/add", element: <AddEditEvent />, adminOnly: true },
  { path: "/events/add/:eventId", element: <AddEditEvent />, adminOnly: true },
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      ...publicRoutes,
            ...protectedRoutes.map((route) => ({
        ...route,
        element: <ProtectedRoute element={route.element} adminOnly={route.adminOnly}/>,
      })),
    ],
  },
  // {
  //   path: "/images/logo.png",
  //   element: <Image />, 
  // },
]);

const App: React.FC = () => {

  //  const path = window.location.pathname;

  // if (path === '/images/logo.png') {
  //   return <Image />;
  // }


  const [loading, setLoading] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadingOut(true); 
      const fadeTimer = setTimeout(() => {
        setLoading(false);
      }, 200); 

      return () => clearTimeout(fadeTimer);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <Box bgcolor={'black'}><SplashScreen fadingOut={fadingOut} /></Box>;
  }

  return (
    <Suspense>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <Elements stripe={stripePromise}>
            <RouterProvider router={router} />
          </Elements>
        </UserProvider>
      </QueryClientProvider>
    </Suspense>
  );
};

export default App;
